import { isAsyncPace } from '../../shared/config';
import type { GameMode } from '../../shared/modes';
import { recapPlan, recapTotalMs } from '../../game/recap';
import { Clock } from '../platform/clock.svelte';
import { stageForPlayback, type RevealPlayback } from '../results/reveal-stages';
import type { SessionRoundResults } from '../session/room-session-state';

interface PresentationInput {
  results: SessionRoundResults | null;
  mode: GameMode;
  revealAt: number | null;
  serverOffset: number;
  visible: boolean;
}

/** App-owned recap playback: one current result, one clock, and one completion cue. */
export class PostMatchPresentation {
  private readonly clock = new Clock('frame');
  private roundId: string | null = $state(null);
  private localStartedAt: number | null = $state(null);
  private shared = $state(false);
  private revealAt: number | null = $state(null);
  /** The current result's recap lengths (`recapPlan`); server gating derives the same total. */
  private tallyMs = $state(0);
  private totalMs = $state(0);
  private serverOffset = $state(0);
  private pendingCue: string | null = $state(null);
  /** The viewer skipped this result's recap; only this client jumps to standings. */
  private skipped = $state(false);
  private ticking = false;

  observe({ results, mode, revealAt, serverOffset, visible }: PresentationInput): void {
    const roundId = results?.roundId ?? null;
    if (this.roundId !== roundId) {
      this.roundId = roundId;
      this.localStartedAt = null;
      this.skipped = false;
    }
    this.shared = mode === 'versus' && results !== null && !isAsyncPace(results.config);
    this.revealAt = revealAt;
    const plan = results ? recapPlan(results) : null;
    this.tallyMs = plan?.tallyMs ?? 0;
    this.totalMs = plan ? recapTotalMs(plan) : 0;
    this.serverOffset = serverOffset;
    if (roundId && visible && !this.shared && this.localStartedAt === null) this.localStartedAt = Date.now();
    if (!roundId) this.pendingCue = null;
  }

  get playback(): RevealPlayback {
    if (this.roundId === null || this.skipped) return { kind: 'settled' };
    if (this.shared) {
      // Old stored rounds have no reveal timestamp; do not invent a fresh shared reveal.
      return this.revealAt === null
        ? { kind: 'settled' }
        : {
            kind: 'shared',
            revealAt: this.revealAt,
            serverNow: this.clock.now + this.serverOffset,
            tallyMs: this.tallyMs,
          };
    }
    return this.localStartedAt === null
      ? { kind: 'settled' }
      : {
          kind: 'local',
          startedAt: this.localStartedAt,
          now: this.clock.now,
          tallyMs: this.tallyMs,
        };
  }

  get settled(): boolean {
    return stageForPlayback(this.playback) === 'standings';
  }

  /** The clock runs until standings and, after a skip, until the shared gate opens. */
  get active(): boolean {
    return !this.settled || this.gated;
  }

  /** Jump this client to standings; a shared Live gate still follows server time. */
  skip(): void {
    this.skipped = true;
  }

  get gated(): boolean {
    return (
      this.shared &&
      this.revealAt !== null &&
      this.clock.now + this.serverOffset < this.revealAt + this.totalMs
    );
  }

  run(active: boolean): void {
    if (this.ticking === active) return;
    this.ticking = active;
    this.clock.run(active);
  }

  completed(roundId: string): void {
    this.pendingCue = roundId;
  }

  /** Called by App's effect; only App performs the audio side effect. */
  takeCue(visible: boolean, results: SessionRoundResults | null, meId: string): 'win' | 'results' | null {
    if (
      !visible ||
      !results ||
      results.roundId !== this.roundId ||
      this.pendingCue !== this.roundId ||
      !this.settled
    )
      return null;
    this.pendingCue = null;
    const top = results.players[0]?.score ?? 0;
    return top > 0 && results.players.some((player) => player.id === meId && player.score === top)
      ? 'win'
      : 'results';
  }

  mount(): () => void {
    this.clock.mount();
    return () => this.dispose();
  }

  dispose(): void {
    this.ticking = false;
    this.clock.dispose();
  }
}
